import { useEffect, useState } from "react";
import { Modal, ConfigProvider, Divider, Select, DatePicker, message } from "antd";
import TextArea from "antd/es/input/TextArea";
import ApiService from "../../utils/ApiService";
import CustomInput from "../CustomInput";
import useUserStore from "../../stores/store";
import { containsAlphabet } from "../../utils/Common";
import { staffPostions, staffGender } from "../../services/mixed-service";
import dayjs from "dayjs";
import useScrollLock from "../../hooks/useScrollLock";

function ViewStaffModal({ openModal, records }) {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [dob, setDob] = useState("");
  const [contact, setContact] = useState("");
  const [gender, setGender] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [address, setAddress] = useState("");
  const [handleOkClicked, setHandleOkClicked] = useState(false);

  // Lock scroll when modal is open
  useScrollLock(openModal);

  const { user, restaurantId, showNotification } = useUserStore();

  const handleOk = async () => {
    setHandleOkClicked(true);
    if (records.isEditMode) {
      /*if (validatePhoneNumber(emergencyContact) === false) {
        showNotification("Emergency contact number is less than 11 digits");
        return;
      }*/
      if (!name || name.trim() === "") {
        showNotification("Staff Name is required");
        return;
      }

      if (!position || position.trim() === "") {
        showNotification("Position is required");
        return;
      }

      if (!dob) {
        showNotification("Date of Birth is required");
        return;
      }

      if (!gender) {
        showNotification("Gender is required");
        return;
      }

      /* 
      Emergency contact name validation
      Emergency contact name must be at least 2 characters long.
      The emergency contact name can not contain only numbers.
      */
      if (emergencyContactName !== "") {
        const trimmedName = emergencyContactName.trim();

        if (trimmedName.length < 2) {
          showNotification("Emergency contact name must be at least 2 characters long.");
          return;
        }

        if (!containsAlphabet(trimmedName)) {
          showNotification("The emergency contact name cannot contain only numbers.");
          return;
        }
      }

      const payload = {
        restaurantId,
        staffId: records.param._id,
        position,
        emergencyContactName,
        emergencyContactNumber: emergencyContact,
        address,
        dob,
        gender,
        name: name.trim(),
      };

      const res = await ApiService.PATCH_ADMIN_STAFF(payload, user.token.token);

      if (res.status === "ok") {
        records.onFinish();
        showNotification("Staff updated successfully", false);
      } else {
        showNotification(res.data.message);
        return;
      }
    } else {
      records.onFinish();
    }
  };
  const handleCancel = () => {
    records.onCancel();
    setHandleOkClicked(false);
  };

  function onHandleDob(date, dateString) {
    setDob(dateString);
  }

  useEffect(() => {
    console.log('-------[records.param]-------', {records, param: records?.param});
    if (records && records?.param) {
      setName(records.param.name || "");
      setPosition(records.param.position || "");
      setDob(records.param.dob || "");
      setContact(records.param.contact || "");
      setGender(records.param.gender || "");
      setEmergencyContactName(records.param.econ_name || "");
      setEmergencyContact(records.param.econ || "");
      setAddress(records.param.address || "");
    }
  }, [records.param]);

  function onHandleStaffName(val) {
    if (val.length > 100) {
      message.warning("Staff name character limit exceeds. Please make it upto 100 characters.");
      return;
    }
    setName(val);
  }

  function onHandleEmergencyContactName(val) {
    if (val.length > 100) {
      message.warning("Emergency contact name character limit exceeds. Please make it upto 100 characters.");
      return;
    }
    setEmergencyContactName(val);
  }

  const handleEmergencyContactChange = (val) => {
    if (/^\d{0,11}$/.test(val)) {
      // Allow up to 11 digits
      setEmergencyContact(val);
    }
  };

  function onHandleAddress(e) {
    const address = e.target.value;
    if (address.length > 200) {
      message.warning("Address character limit exceeds. Please make it upto 200 characters.");
      return;
    }
    setAddress(address);
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "#FFD700",
            defaultHoverColor: "#000000",
            fontFamily: "ubold",
          },
        },
      }}
    >
      <Modal
        maskClosable={false}
        open={openModal}
        okButtonProps={{
          style: {
            backgroundColor: "#FFD700",
            color: "#000000",
            fontFamily: "ubold",
          },
        }}
        onOk={handleOk}
        okText={records.isEditMode ? "Edit Staff" : "Ok"}
        onCancel={handleCancel}
      >
        <div className="flex flex-col font-uregular">
          <div className="text-2xl font-ubold">{records.isEditMode ? "Edit Staff" : "View Staff"}</div>
          <Divider />
          {/* Name */}
          <CustomInput
            value={name}
            setValue={onHandleStaffName}
            disabled={!records.isEditMode}
            showPadding={false}
            label={
              <div className="flex items-center">
                <span>Staff Name</span>
                <span className="font-ubold text-red-700 ml-1">*</span>
              </div>
            }
          />

          {/* Position and DOB */}
          <div className="flex flex-row justify-between">
            <div className="w-1/2 mr-2">
              <div className="ml-1 text-sm font-uregular flex items-center">
                <span>Position</span>
                <span className="font-ubold text-red-700 ml-1">*</span>
              </div>

              <Select
                disabled={!records.isEditMode}
                value={position}
                className="w-full"
                onChange={setPosition}
                options={staffPostions}
              />
            </div>
            <div className="w-1/2 ml-2">
              <div className="ml-1 text-sm font-uregular flex items-center">
                <span>DOB</span>
                <span className="font-ubold text-red-700 ml-1">*</span>
              </div>

              <DatePicker
                className={"h-[30px] w-full focus:outline-2 focus:outline-offset-2 focus:black"}
                disabled={!records.isEditMode}
                value={dob ? dayjs(dob) : null}
                onChange={onHandleDob}
                disabledDate={(current) => {
                  // Disable today and future dates
                  return current && current >= dayjs().startOf("day");
                }}
              />
            </div>
          </div>

          {/* Contact and Gender */}
          <div className="flex flex-row justify-between">
            <div className="w-1/2 mr-2">
              <CustomInput
                value={contact}
                disabled={true}
                showPadding={false}
                label={
                  <div className="flex items-center">
                    <span>Contact</span>
                    <span className="font-ubold text-red-700 ml-1">*</span>
                  </div>
                }
              />
            </div>
            <div className="w-1/2 ml-2">
              <div className="ml-1 text-sm font-uregular flex items-center">
                <span>Gender</span>
                <span className="font-ubold text-red-700 ml-1">*</span>
              </div>

              <Select
                disabled={!records.isEditMode}
                value={gender}
                className="w-full"
                onChange={setGender}
                options={staffGender}
              />
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="flex flex-row justify-between">
            <div className="w-1/2 mr-2">
              <CustomInput
                value={emergencyContactName}
                setValue={onHandleEmergencyContactName}
                disabled={!records.isEditMode}
                showPadding={false}
                label={
                  <div className="flex items-center">
                    <span>Emergency Contact Name</span>
                  </div>
                }
              />
            </div>
            <div className="w-1/2 ml-2">
              <CustomInput
                value={emergencyContact}
                setValue={handleEmergencyContactChange}
                disabled={!records.isEditMode}
                showPadding={false}
                onlyNumeric={true}
                label={
                  <div className="flex items-center">
                    <span>Emergency Contact</span>
                  </div>
                }
              />
            </div>
          </div>

          {/* Address */}
          {/* Address */}
          <div className="flex flex-col mb-[12px]">
            <div className="ml-[4px] text-sm font-uregular flex flex-row">
              <div>Address</div>
            </div>
            <TextArea
              rows={5}
              placeholder="Max 100 words"
              value={address}
              onChange={onHandleAddress}
              disabled={!records.isEditMode}
            />
          </div>
          <Divider />
        </div>
      </Modal>
    </ConfigProvider>
  );
}

export default ViewStaffModal;
